// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSharedMqtt } from './SharedMqttProvider';
import { useAuth } from './AuthContext';

const MqttActivityContext = createContext();

// Maximum entries to keep in localStorage to prevent quota issues
const MAX_ACTIVITY_ENTRIES = 50;
const MAX_DEVICE_INFO_ENTRIES = 20;

// In-memory caps (more generous than the localStorage ones — these bound the
// React state itself, which previously grew without limit for the lifetime of
// the tab as devices came and went).
const MAX_IN_MEMORY_ACTIVITY_ENTRIES = 500;
const MAX_IN_MEMORY_DEVICE_INFO_ENTRIES = 300;
const IN_MEMORY_STALE_MS = 24 * 60 * 60 * 1000; // drop entries unseen for 24h

// Prune a map of { key: isoString | { last_seen } } entries: drop anything
// older than IN_MEMORY_STALE_MS, then cap to maxEntries (newest kept). Returns
// the original object when nothing changed so React state stays referentially
// stable.
function pruneTimestampMap(map, maxEntries, getTime) {
  const now = Date.now();
  const entries = Object.entries(map);
  const fresh = entries.filter(([, v]) => {
    const t = getTime(v);
    return Number.isFinite(t) && now - t <= IN_MEMORY_STALE_MS;
  });
  let kept = fresh;
  if (fresh.length > maxEntries) {
    kept = fresh.sort((a, b) => getTime(b[1]) - getTime(a[1])).slice(0, maxEntries);
  }
  return kept.length === entries.length ? map : Object.fromEntries(kept);
}

export function useMqttActivity() {
  return useContext(MqttActivityContext);
}

export function MqttActivityProvider({ children }) {
  const { isAuthenticated } = useAuth();

  // Refs for debounced saves
  const saveActivityTimeoutRef = useRef(null);
  const saveDeviceInfoTimeoutRef = useRef(null);
  const pendingActivitySaveRef = useRef(null);
  const pendingDeviceInfoSaveRef = useRef(null);

  // Safe localStorage save with quota handling
  const safeLocalStorageSave = useCallback((key, data, maxEntries) => {
    try {
      // Limit entries to prevent quota issues
      let limitedData = data;
      const entries = Object.entries(data);

      if (entries.length > maxEntries) {
        // Keep only the most recent entries based on value (timestamp)
        const sorted = entries.sort((a, b) => {
          try {
            const timeA = new Date(a[1]?.last_seen || a[1]).getTime();
            const timeB = new Date(b[1]?.last_seen || b[1]).getTime();
            return timeB - timeA; // Newest first
          } catch {
            return 0;
          }
        });
        limitedData = Object.fromEntries(sorted.slice(0, maxEntries));
      }

      localStorage.setItem(key, JSON.stringify(limitedData));
      return true;
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        // Clear old data and try again with minimal data
        try {
          localStorage.removeItem(key);
          const entries = Object.entries(data);
          const minimal = Object.fromEntries(entries.slice(0, 10)); // Keep only 10 entries
          localStorage.setItem(key, JSON.stringify(minimal));
        } catch {
          // If still failing, just clear it
          localStorage.removeItem(key);
        }
      }
      return false;
    }
  }, []);

  // Debounced save for activity map (saves at most once per second)
  const debouncedSaveActivityMap = useCallback(
    (data) => {
      pendingActivitySaveRef.current = data;

      if (saveActivityTimeoutRef.current) {
        return; // Already scheduled
      }

      saveActivityTimeoutRef.current = setTimeout(() => {
        if (pendingActivitySaveRef.current) {
          safeLocalStorageSave('activityMap', pendingActivitySaveRef.current, MAX_ACTIVITY_ENTRIES);
        }
        saveActivityTimeoutRef.current = null;
      }, 1000);
    },
    [safeLocalStorageSave]
  );

  // Debounced save for device info map (saves at most once per 2 seconds)
  const debouncedSaveDeviceInfoMap = useCallback(
    (data) => {
      pendingDeviceInfoSaveRef.current = data;

      if (saveDeviceInfoTimeoutRef.current) {
        return; // Already scheduled
      }

      saveDeviceInfoTimeoutRef.current = setTimeout(() => {
        if (pendingDeviceInfoSaveRef.current) {
          safeLocalStorageSave(
            'deviceInfoMap',
            pendingDeviceInfoSaveRef.current,
            MAX_DEVICE_INFO_ENTRIES
          );
        }
        saveDeviceInfoTimeoutRef.current = null;
      }, 2000);
    },
    [safeLocalStorageSave]
  );

  // Simple activity tracking - no pruning needed
  const loadInitialActivityMap = () => {
    try {
      const raw = localStorage.getItem('activityMap');
      const data = raw ? JSON.parse(raw) : {};

      // AGGRESSIVE FIX: Clear any data older than 20 seconds on load
      const now = Date.now();
      const STALE_THRESHOLD = 20000; // 20 seconds - match backend timeout
      const cleanedData = {};
      let clearedCount = 0;

      Object.entries(data).forEach(([key, value]) => {
        try {
          const lastSeenTime = new Date(value).getTime();
          const timeSinceLastSeen = now - lastSeenTime;

          if (timeSinceLastSeen <= STALE_THRESHOLD) {
            cleanedData[key] = value; // Keep recent entries
          } else {
            clearedCount++;
          }
        } catch (e) {
          clearedCount++;
        }
      });

      // Also limit to max entries
      const entries = Object.entries(cleanedData);
      if (entries.length > MAX_ACTIVITY_ENTRIES) {
        const limited = Object.fromEntries(entries.slice(0, MAX_ACTIVITY_ENTRIES));
        if (clearedCount > 0 || entries.length > MAX_ACTIVITY_ENTRIES) {
          try {
            localStorage.setItem('activityMap', JSON.stringify(limited));
          } catch {
            /* ignore */
          }
        }
        return limited;
      }

      if (clearedCount > 0) {
        try {
          localStorage.setItem('activityMap', JSON.stringify(cleanedData));
        } catch {
          /* ignore */
        }
      }

      return cleanedData;
    } catch (e) {
      // If there's any error, clear localStorage and start fresh
      try {
        localStorage.removeItem('activityMap');
      } catch {
        /* ignore */
      }
      return {};
    }
  };

  const loadInitialDeviceInfoMap = () => {
    try {
      const raw = localStorage.getItem('deviceInfoMap');
      const data = raw ? JSON.parse(raw) : {};

      // Limit initial load to max entries
      const entries = Object.entries(data);
      if (entries.length > MAX_DEVICE_INFO_ENTRIES) {
        const limited = Object.fromEntries(entries.slice(0, MAX_DEVICE_INFO_ENTRIES));
        try {
          localStorage.setItem('deviceInfoMap', JSON.stringify(limited));
        } catch {
          /* ignore */
        }
        return limited;
      }

      return data;
    } catch (e) {
      // If there's any error, clear localStorage and start fresh
      try {
        localStorage.removeItem('deviceInfoMap');
      } catch {
        /* ignore */
      }
      return {};
    }
  };

  const [activityMap, setActivityMap] = useState(loadInitialActivityMap); // { deviceId: lastSeen ISO string }
  const [serialToDeviceIdMap, setSerialToDeviceIdMap] = useState({}); // { serial_number: deviceId }
  const [stargateList, setStargateList] = useState([]); // Stargate list state for correlation
  const [deviceInfoMap, setDeviceInfoMap] = useState(loadInitialDeviceInfoMap); // { deviceId: lastMqttPayload }
  // Global status map: { serial_number: 'green' | 'red' }
  const [stargateStatusMap, setStargateStatusMap] = useState({}); // { serial_number: 'green' | 'red' }
  // Periodic tick for forcing UI refresh - use a single 5-second timer
  // (removed the duplicate 2s timer that was causing excessive re-renders)
  const [tick, setTick] = useState(0);
  const tickIntervalRef = useRef(null);
  useEffect(() => {
    // Only run timer if authenticated
    if (!isAuthenticated) {
      return;
    }

    tickIntervalRef.current = setInterval(() => setTick((t) => t + 1), 5000);
    return () => {
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
    };
  }, [isAuthenticated]);

  // Every ~5 minutes, prune the in-memory maps so a long-lived tab can't
  // accumulate entries for devices that no longer report.
  useEffect(() => {
    if (tick === 0 || tick % 60 !== 0) return;
    setActivityMap((prev) =>
      pruneTimestampMap(prev, MAX_IN_MEMORY_ACTIVITY_ENTRIES, (v) => new Date(v).getTime())
    );
    setDeviceInfoMap((prev) =>
      pruneTimestampMap(prev, MAX_IN_MEMORY_DEVICE_INFO_ENTRIES, (v) =>
        new Date(v?.last_seen || 0).getTime()
      )
    );
    // serialToDeviceIdMap / stargateStatusMap are keyed by known stargates and
    // stay small; deletions are already handled via device_deleted events.
  }, [tick]);

  // Use shared MQTT connection
  const sharedMqtt = useSharedMqtt();
  const isMqttConnected = sharedMqtt?.isConnected || false;
  const addMessageListener = sharedMqtt?.addMessageListener || (() => {});
  const removeMessageListener = sharedMqtt?.removeMessageListener || (() => {});

  // Synchronize serialToDeviceIdMap whenever stargateList changes.
  // Removed deviceInfoMap and serialToDeviceIdMap from deps - they change on every MQTT
  // message and caused cascading re-renders. The onMessage handler already maintains
  // the serialToDeviceIdMap mapping directly when processing messages.
  const deviceInfoMapRef = useRef(deviceInfoMap);
  deviceInfoMapRef.current = deviceInfoMap;

  useEffect(() => {
    if (!stargateList || stargateList.length === 0) return;

    const currentDeviceInfoMap = deviceInfoMapRef.current;
    const updates = {};
    stargateList.forEach((sg) => {
      const deviceId = Object.keys(currentDeviceInfoMap).find((id) => {
        const info = currentDeviceInfoMap[id];
        if (!info) return false;
        return (
          (sg.ip_address && info.ip_address && sg.ip_address === info.ip_address) ||
          (sg.friendly_name && info.friendly_name && sg.friendly_name === info.friendly_name)
        );
      });
      if (deviceId && sg.serial_number) {
        updates[sg.serial_number] = deviceId;
      }
    });

    if (Object.keys(updates).length > 0) {
      setSerialToDeviceIdMap((prev) => {
        // Only update if there are actual changes
        const hasChanges = Object.entries(updates).some(([key, val]) => prev[key] !== val);
        return hasChanges ? { ...prev, ...updates } : prev;
      });
    }
  }, [stargateList]);

  // ── Batched message processing ──
  // Instead of calling setState on every incoming MQTT message (which triggers a
  // re-render per message and locks the browser when hundreds arrive at once on
  // reconnect), we accumulate changes in plain objects (refs) and flush them into
  // React state on a single timer.
  const pendingActivityRef = useRef({});
  const pendingDeviceInfoRef = useRef({});
  const pendingSerialMapRef = useRef({});
  const pendingDeletionsRef = useRef([]);
  const flushTimerRef = useRef(null);

  const flushPendingUpdates = useCallback(() => {
    flushTimerRef.current = null;

    const activityBatch = pendingActivityRef.current;
    const deviceInfoBatch = pendingDeviceInfoRef.current;
    const serialMapBatch = pendingSerialMapRef.current;
    const deletions = pendingDeletionsRef.current;

    pendingActivityRef.current = {};
    pendingDeviceInfoRef.current = {};
    pendingSerialMapRef.current = {};
    pendingDeletionsRef.current = [];

    const hasActivity = Object.keys(activityBatch).length > 0;
    const hasDeviceInfo = Object.keys(deviceInfoBatch).length > 0;
    const hasSerialMap = Object.keys(serialMapBatch).length > 0;
    const hasDeletions = deletions.length > 0;

    if (!hasActivity && !hasDeviceInfo && !hasSerialMap && !hasDeletions) return;

    if (hasActivity || hasDeletions) {
      setActivityMap((prev) => {
        const next = { ...prev, ...activityBatch };
        for (const d of deletions) {
          delete next[d];
        }
        debouncedSaveActivityMap(next);
        return next;
      });
    }

    if (hasDeviceInfo || hasDeletions) {
      setDeviceInfoMap((prev) => {
        const next = { ...prev, ...deviceInfoBatch };
        for (const d of deletions) {
          delete next[d];
        }
        debouncedSaveDeviceInfoMap(next);
        return next;
      });
    }

    if (hasSerialMap) {
      setSerialToDeviceIdMap((prev) => ({ ...prev, ...serialMapBatch }));
    }

    if (hasDeletions) {
      setStargateStatusMap((prev) => {
        const next = { ...prev };
        for (const d of deletions) {
          delete next[d];
        }
        return next;
      });
    }
  }, [debouncedSaveActivityMap, debouncedSaveDeviceInfoMap]);

  const scheduleFlush = useCallback(() => {
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushPendingUpdates, 100);
    }
  }, [flushPendingUpdates]);

  // Process incoming MQTT messages — lightweight, no React state touched.
  const onMessage = useCallback(
    (msg) => {
      try {
        // Handle device deletion event
        if (msg.topic && msg.topic.startsWith('starfleet/device_deleted/')) {
          const deletedSerial = msg.topic.split('/').pop();
          pendingDeletionsRef.current.push(deletedSerial);
          scheduleFlush();
          return;
        }

        let deviceId = msg.topic?.split('/').pop() || '';

        let payload = msg.payload;
        if (payload && typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch {
            payload = { raw: payload };
          }
        } else if (!payload) {
          payload = {};
        }

        if (payload.serial_number) {
          deviceId = payload.serial_number;
        }

        if (!deviceId) return;

        const nowISO = new Date().toISOString();
        const ipAddress = payload.ip_address || (msg.ip_address ? String(msg.ip_address) : null);

        // Accumulate into pending batches (plain object writes — zero React overhead)
        pendingActivityRef.current[deviceId] = nowISO;
        if (ipAddress) pendingActivityRef.current[ipAddress] = nowISO;
        if (payload.serial_number && payload.serial_number !== deviceId) {
          pendingActivityRef.current[payload.serial_number] = nowISO;
        }

        pendingDeviceInfoRef.current[deviceId] = {
          ...payload,
          deviceId,
          ip_address: ipAddress,
          last_seen: nowISO,
        };

        if (payload.serial_number && stargateList?.length > 0) {
          const match = stargateList.find(
            (sg) =>
              sg.serial_number === payload.serial_number ||
              (sg.ip_address && sg.ip_address === ipAddress) ||
              (sg.friendly_name && sg.friendly_name === payload.friendly_name)
          );
          if (match?.serial_number) {
            pendingSerialMapRef.current[match.serial_number] = deviceId;
          }
        }

        scheduleFlush();
      } catch (err) {
        console.error('Error processing MQTT message:', err);
      }
    },
    [stargateList, scheduleFlush]
  );

  // Use shared MQTT connection for messages
  const listenerIdRef = useRef(null);
  useEffect(() => {
    const id = addMessageListener(onMessage, 'MqttActivityContext', {
      permanent: true,
    });
    if (id) listenerIdRef.current = id;

    return () => {
      if (listenerIdRef.current) {
        removeMessageListener(listenerIdRef.current);
        listenerIdRef.current = null;
      }
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (saveActivityTimeoutRef.current) {
        clearTimeout(saveActivityTimeoutRef.current);
      }
      if (saveDeviceInfoTimeoutRef.current) {
        clearTimeout(saveDeviceInfoTimeoutRef.current);
      }
    };
  }, [onMessage, addMessageListener, removeMessageListener]);

  // Simple 20-second rule: green if message received within 20s, red otherwise
  const getStargateStatusByIp = (ip_address) => {
    if (!ip_address) {
      return 'red'; // Default to red (disconnected)
    }

    const now = Date.now();
    const TIMEOUT_MS = 20000; // 20 seconds - FIXED to match backend

    // Try multiple approaches to find activity data:

    // 1. Direct IP address lookup in activity map (this should work after my fix)
    if (activityMap[ip_address]) {
      const lastSeen = activityMap[ip_address];
      const lastSeenTime = new Date(lastSeen).getTime();
      const timeSinceLastSeen = now - lastSeenTime;
      const status = timeSinceLastSeen <= TIMEOUT_MS ? 'green' : 'red';
      return status;
    }

    // 2. Find the device in deviceInfoMap that matches the given IP address
    const deviceId = Object.entries(deviceInfoMap).find(([id, info]) => {
      if (!info) return false;

      // Check direct IP match
      const ipMatch =
        info.ip_address && String(info.ip_address).trim() === String(ip_address).trim();

      // Check if any stargate in the list matches this IP
      const stargateMatch = stargateList.some(
        (sg) => sg.ip_address && String(sg.ip_address).trim() === String(ip_address).trim()
      );

      return ipMatch || stargateMatch;
    })?.[0]; // Get the device ID from the first matching entry

    // 3. Check if we found a device ID and have activity for it
    if (deviceId && activityMap[deviceId]) {
      const lastSeen = activityMap[deviceId];
      const lastSeenTime = new Date(lastSeen).getTime();
      const timeSinceLastSeen = now - lastSeenTime;
      const status = timeSinceLastSeen <= TIMEOUT_MS ? 'green' : 'red';
      return status;
    }

    // 4. Try to find by checking if any activity map key contains this IP
    const ipBasedKey = Object.keys(activityMap).find((key) => {
      // Check if the key itself is the IP or contains the IP
      return key === ip_address || key.includes(ip_address);
    });

    if (ipBasedKey && activityMap[ipBasedKey]) {
      const lastSeen = activityMap[ipBasedKey];
      const lastSeenTime = new Date(lastSeen).getTime();
      const timeSinceLastSeen = now - lastSeenTime;
      const status = timeSinceLastSeen <= TIMEOUT_MS ? 'green' : 'red';
      return status;
    }
    return 'red'; // Default to disconnected
  };

  // Simple 20-second rule by serial number - direct lookup
  const getStargateStatusBySerial = (serial_number) => {
    if (!serial_number) {
      return 'red'; // Default to red (disconnected)
    }

    const now = Date.now();
    const TIMEOUT_MS = 20000; // 20 seconds - FIXED to match backend

    // Check if we have activity for this serial number directly
    if (activityMap[serial_number]) {
      const lastSeen = activityMap[serial_number];
      const lastSeenTime = new Date(lastSeen).getTime();
      const timeSinceLastSeen = now - lastSeenTime;
      const status = timeSinceLastSeen <= TIMEOUT_MS ? 'green' : 'red';
      return status;
    }

    // Check if we have it mapped through serialToDeviceIdMap
    const deviceId = serialToDeviceIdMap[serial_number];
    if (deviceId && activityMap[deviceId]) {
      const lastSeen = activityMap[deviceId];
      const lastSeenTime = new Date(lastSeen).getTime();
      const timeSinceLastSeen = now - lastSeenTime;
      const status = timeSinceLastSeen <= TIMEOUT_MS ? 'green' : 'red';
      return status;
    }

    // Fallback to IP-based lookup if direct methods fail
    const stargate = stargateList.find((sg) => sg.serial_number === serial_number);
    if (stargate?.ip_address) {
      const status = getStargateStatusByIp(stargate.ip_address);
      return status;
    }

    return 'red'; // Default to disconnected
  };

  // Simple status map computation using 20-second rule.
  // Only recompute on the 5-second tick or when stargateList changes.
  // Previously this ran on every activityMap/deviceInfoMap change (every MQTT message),
  // causing unnecessary computation and potential re-renders.
  const activityMapRef = useRef(activityMap);
  activityMapRef.current = activityMap;
  const serialToDeviceIdMapRef = useRef(serialToDeviceIdMap);
  serialToDeviceIdMapRef.current = serialToDeviceIdMap;

  useEffect(() => {
    if (!stargateList || stargateList.length === 0) return;

    const newStatusMap = {};

    stargateList.forEach((sg) => {
      if (!sg.serial_number) return;

      // Use the simplified status function
      newStatusMap[sg.serial_number] = getStargateStatusBySerial(sg.serial_number);
    });

    // Update status map if there are changes
    setStargateStatusMap((prev) => {
      const hasChanges =
        Object.keys(newStatusMap).some((key) => prev[key] !== newStatusMap[key]) ||
        Object.keys(prev).length !== Object.keys(newStatusMap).length;

      return hasChanges ? newStatusMap : prev;
    });
  }, [tick, stargateList]);

  // Deprecated: get status by serial number (for backward compatibility)
  const getStargateStatus = (serialNumber) => {
    console.warn(
      '[DEPRECATED] getStargateStatus(serialNumber) is deprecated. Use getStargateStatusBySerial(serial_number) instead.'
    );
    return getStargateStatusBySerial(serialNumber);
  };

  // Removed duplicate 5-second status refresh timer - the single 5s tick timer above handles this.
  // Having two timers (2s + 5s) both calling setTick caused unnecessary re-renders.

  // Function to clear stale activity data
  const clearStaleActivityData = useCallback(() => {
    const now = Date.now();
    const STALE_THRESHOLD = 20000; // 20 seconds - match backend timeout - anything older is considered stale

    setActivityMap((prev) => {
      const newMap = {};
      let clearedCount = 0;

      Object.entries(prev).forEach(([key, value]) => {
        try {
          const lastSeenTime = new Date(value).getTime();
          const timeSinceLastSeen = now - lastSeenTime;

          if (timeSinceLastSeen <= STALE_THRESHOLD) {
            newMap[key] = value; // Keep recent entries
          } else {
            clearedCount++;
          }
        } catch (e) {
          clearedCount++;
        }
      });

      if (clearedCount > 0) {
        safeLocalStorageSave('activityMap', newMap, MAX_ACTIVITY_ENTRIES);
      }

      return newMap;
    });
  }, [safeLocalStorageSave]);

  // Debug function to help troubleshoot status issues
  const debugStargateStatus = (ip_address, serial_number) => {
    const now = Date.now();
    const TIMEOUT_MS = 20000;

    console.group(`🔍 Debug Stargate Status - IP: ${ip_address}, Serial: ${serial_number}`);

    // Check activityMap entries
    console.log('📊 Activity Map entries:');
    console.log('- Direct IP lookup:', activityMap[ip_address]);
    console.log('- Direct serial lookup:', activityMap[serial_number]);
    console.log('- Full activity map:', activityMap);

    // Check deviceInfoMap
    const deviceId = Object.entries(deviceInfoMap).find(([id, info]) => {
      if (!info) return false;
      return (
        (info.ip_address && String(info.ip_address).trim() === String(ip_address).trim()) ||
        (info.serial_number && info.serial_number === serial_number)
      );
    });
    console.log('📱 Device Info Match:', deviceId);
    if (deviceId?.[0] && activityMap[deviceId[0]]) {
      console.log('- Activity for device ID:', activityMap[deviceId[0]]);
    }

    // Check serialToDeviceIdMap
    console.log('🔗 Serial to Device ID mapping:', serialToDeviceIdMap[serial_number]);

    // Calculate time differences
    if (activityMap[ip_address]) {
      const lastSeen = new Date(activityMap[ip_address]).getTime();
      const timeDiff = now - lastSeen;
      console.log(
        `⏰ IP-based activity: ${timeDiff}ms ago (${timeDiff > TIMEOUT_MS ? 'EXPIRED' : 'VALID'})`
      );
    }

    if (activityMap[serial_number]) {
      const lastSeen = new Date(activityMap[serial_number]).getTime();
      const timeDiff = now - lastSeen;
      console.log(
        `⏰ Serial-based activity: ${timeDiff}ms ago (${timeDiff > TIMEOUT_MS ? 'EXPIRED' : 'VALID'})`
      );
    }

    // Final status
    const status = getStargateStatusByIp(ip_address);
    console.log(`🚨 Final Status: ${status}`);

    console.groupEnd();
    return status;
  };

  // Listen for logout events to clear all data and pending buffers
  useEffect(() => {
    const handleLogout = () => {
      console.log('[MqttActivity] Logout event received, clearing all data');
      // Clear all state
      setActivityMap({});
      setDeviceInfoMap({});
      setSerialToDeviceIdMap({});
      setStargateStatusMap({});
      // Clear localStorage
      localStorage.removeItem('activityMap');
      localStorage.removeItem('deviceInfoMap');
      // Clear pending batches so stale data doesn't leak into the next session
      pendingActivityRef.current = {};
      pendingDeviceInfoRef.current = {};
      pendingSerialMapRef.current = {};
      pendingDeletionsRef.current = [];
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };

    window.addEventListener('user-logout', handleLogout);
    window.addEventListener('session-timeout', handleLogout);

    return () => {
      window.removeEventListener('user-logout', handleLogout);
      window.removeEventListener('session-timeout', handleLogout);
    };
  }, []);

  // Expose debug functions globally for easy console access
  useEffect(() => {
    window.starfleetDebug = {
      debugStatus: debugStargateStatus,
      clearStale: clearStaleActivityData,
      forceCleanAll: () => {
        // console.log('🧹 Force clearing ALL MQTT activity data...');
        localStorage.removeItem('activityMap');
        localStorage.removeItem('deviceInfoMap');
        setActivityMap({});
        setDeviceInfoMap({});
        setSerialToDeviceIdMap({});
        console.log('✅ All MQTT data cleared. Page will refresh...');
        setTimeout(() => window.location.reload(), 1000);
      },
      showActivityMap: () => {
        console.log('📊 Current Activity Map:', activityMap);
        console.log('📱 Device Info Map:', deviceInfoMap);
        console.log('🔗 Serial to Device ID Map:', serialToDeviceIdMap);
        console.log('🚦 Status Map:', stargateStatusMap);
      },
    };
    return () => {
      delete window.starfleetDebug;
    };
  }, [
    activityMap,
    deviceInfoMap,
    serialToDeviceIdMap,
    stargateStatusMap,
    debugStargateStatus,
    clearStaleActivityData,
  ]);

  return (
    <MqttActivityContext.Provider
      value={useMemo(
        () => ({
          activityMap,
          serialToDeviceIdMap,
          stargateList,
          setStargateList,
          getStargateStatus,
          getStargateStatusByIp,
          getStargateStatusBySerial,
          stargateStatusMap,
          tick,
          debugStargateStatus,
          clearStaleActivityData,
        }),
        [
          activityMap,
          serialToDeviceIdMap,
          stargateList,
          getStargateStatus,
          getStargateStatusByIp,
          getStargateStatusBySerial,
          stargateStatusMap,
          tick,
          debugStargateStatus,
          clearStaleActivityData,
        ]
      )}
    >
      {children}
    </MqttActivityContext.Provider>
  );
}
